import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getKpiTimelineAggregatedValues } from '@/features/charts/api/get-charts-for-project';
import { ChartFromKpi } from '@/features/charts/components/chart-from-kpi';
import {
  AggregationType,
  ChartType,
} from '@/features/charts/components/create-chart-type-selection';
import { useChartDataForKpi } from '@/features/charts/hooks/use-chart-configs-for-project';
import {
  getMonthsBackForPeriodType,
  PeriodType,
  periodTypeToAggregationInterval,
} from '@/features/forecasts/api/get-kpi-forecasts';
import { ForecastPeriodSelector } from '@/features/forecasts/components/forecast-period-selector';
import { useForecastsFilterStore } from '@/features/forecasts/hooks/use-forecasts-hub-filter';
import { useKpiForecasts } from '@/features/forecasts/hooks/use-kpi-forecasts';
import { TimelineChartContainer } from '@/features/insights/components/timeline-chart-container';
import { isDerivativeKpi } from '@/features/projects/shared/utils/kpi-utils';
import { getKpiUnitTranslationPlural } from '@/shared/hooks/use-kpi-unit-translation';
import {
  KpiTrackingEntry,
  KpiWithMetrics,
  ProjectType,
} from '@/shared/types/api';

export const ForecastChart = ({
  kpi,
  projectId,
  matchingProject,
  justCard,
}: {
  kpi?: KpiWithMetrics;
  projectId: number | undefined;
  matchingProject?: { id: number; type: ProjectType } | null;
  justCard?: boolean;
}) => {
  if (!kpi || justCard) return <TimelineChartContainer />;

  return (
    <TimelineChart
      kpi={kpi}
      projectId={projectId}
      matchingProject={matchingProject}
    />
  );
};

/** Maps the selected forecast period to an aggregation period supported by the timeline API. */
function determineAggregationPeriodForChart(
  selectedPeriod: PeriodType,
): 'daily' | 'weekly' | 'monthly' {
  const interval = periodTypeToAggregationInterval(selectedPeriod);
  // The aggregated-values RPC only supports daily/weekly/monthly;
  // for quarterly and yearly we fall back to monthly aggregation.
  if (interval === 'quarterly' || interval === 'yearly') return 'monthly';
  return interval;
}

const TimelineChart = ({
  kpi,
  projectId,
  matchingProject,
}: {
  kpi: KpiWithMetrics;
  projectId: number | undefined;
  matchingProject?: { id: number; type: ProjectType } | null;
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('monthly');
  const { t: tKpiTypes } = useTranslation('kpiTypes', { nsMode: 'fallback' });
  const { subprojectIds } = useForecastsFilterStore();

  const kpiVariation = isDerivativeKpi(kpi) ? 'derivative' : 'standard';

  // Check if this is a main project and no subproject filter is active
  const isMainProject = matchingProject?.type === 'project';
  const isSubprojectFiltered = (subprojectIds?.length ?? 0) > 0;
  const shouldAggregate =
    isMainProject &&
    !isSubprojectFiltered &&
    !!projectId &&
    !isDerivativeKpi(kpi);

  // Determine aggregation period based on selected period
  const aggregationPeriod = determineAggregationPeriodForChart(selectedPeriod);

  // Fetch aggregated timeline data for main project view
  const {
    data: aggregatedEntries,
    isLoading: isAggregatedLoading,
    isError: isAggregatedError,
  } = useQuery({
    queryKey: [
      'kpiTimelineAggregatedValues',
      projectId,
      kpi.id,
      aggregationPeriod,
    ],
    queryFn: () =>
      getKpiTimelineAggregatedValues({
        projectId: projectId!,
        kpiId: kpi.id,
        period: aggregationPeriod,
      }),
    enabled: shouldAggregate,
  });

  const projectIdForEntries = shouldAggregate ? undefined : projectId;

  const {
    data: kpiWithData,
    isLoading,
    isError,
  } = useChartDataForKpi({
    id: kpi.id,
    projectId: projectIdForEntries,
    monthsBack: getMonthsBackForPeriodType(selectedPeriod),
    kpi,
  });

  const { data: forecastsData } = useKpiForecasts(
    kpi.id,
    projectId,
    kpiVariation,
  );

  // Handle aggregated timeline rendering
  if (shouldAggregate) {
    if (isAggregatedLoading || isAggregatedError || !aggregatedEntries) {
      return <TimelineChartContainer />;
    }

    // Check if we have data from multiple subprojects
    const hasMultipleSubprojects = aggregatedEntries.some(
      (entry) => entry.subprojectCount > 1,
    );

    // If only single subproject data, fall back to regular rendering
    if (!hasMultipleSubprojects && kpiWithData) {
      if (isLoading || isError || !forecastsData)
        return <TimelineChartContainer />;

      const selectedForecastData = forecastsData?.forecasts.find(
        (f) => f.period_type === selectedPeriod,
      );

      return (
        <TimelineChartContainer>
          <ChartFromKpi
            chartType={ChartType.LINE}
            title={kpi.name}
            description={getKpiUnitTranslationPlural(tKpiTypes, kpi.unit)}
            kpi={kpiWithData}
            aggregationType={AggregationType.TIMELINE}
            aggregationInterval={periodTypeToAggregationInterval(
              selectedPeriod,
            )}
            chartClassName="h-[225px]"
            unit={kpi.unit}
            forecastData={selectedForecastData?.forecasts}
            periodSelector={
              <ForecastPeriodSelector
                currentPeriod={selectedPeriod}
                onUpdatePeriod={setSelectedPeriod}
              />
            }
            periodType={selectedPeriod}
          />
        </TimelineChartContainer>
      );
    }

    // Convert aggregated entries to synthetic KpiTrackingEntry format
    const syntheticEntries: KpiTrackingEntry[] = aggregatedEntries.map(
      (entry, index) => ({
        id: index,
        kpi_id: kpi.id,
        project_id: projectId!,
        report_id: 0,
        value: entry.aggregatedValue,
        created_at: entry.periodStart,
        updated_at: null,
        created_by: null,
        updated_by: null,
      }),
    );

    const aggregatedKpiWithData = {
      ...kpi,
      entries: syntheticEntries,
    };

    if (!aggregatedKpiWithData || !forecastsData)
      return <TimelineChartContainer />;

    const selectedForecastData = forecastsData?.forecasts.find(
      (f) => f.period_type === selectedPeriod,
    );

    return (
      <TimelineChartContainer>
        <ChartFromKpi
          chartType={ChartType.LINE}
          title={kpi.name}
          description={getKpiUnitTranslationPlural(tKpiTypes, kpi.unit)}
          kpi={aggregatedKpiWithData}
          aggregationType={AggregationType.TIMELINE}
          aggregationInterval={periodTypeToAggregationInterval(selectedPeriod)}
          chartClassName="h-[225px]"
          unit={kpi.unit}
          forecastData={selectedForecastData?.forecasts}
          periodSelector={
            <ForecastPeriodSelector
              currentPeriod={selectedPeriod}
              onUpdatePeriod={setSelectedPeriod}
            />
          }
          periodType={selectedPeriod}
        />
      </TimelineChartContainer>
    );
  }

  // Regular rendering for non-main projects or when subproject filter is active
  if (isLoading || isError || !kpiWithData || !forecastsData)
    return <TimelineChartContainer />;

  const selectedForecastData = forecastsData?.forecasts.find(
    (f) => f.period_type === selectedPeriod,
  );

  return (
    <TimelineChartContainer>
      <ChartFromKpi
        chartType={ChartType.LINE}
        title={kpi.name}
        description={getKpiUnitTranslationPlural(tKpiTypes, kpi.unit)}
        kpi={kpiWithData}
        aggregationType={AggregationType.TIMELINE}
        aggregationInterval={periodTypeToAggregationInterval(selectedPeriod)}
        chartClassName="h-[225px]"
        unit={kpi.unit}
        forecastData={selectedForecastData?.forecasts}
        periodSelector={
          <ForecastPeriodSelector
            currentPeriod={selectedPeriod}
            onUpdatePeriod={setSelectedPeriod}
          />
        }
        periodType={selectedPeriod}
      />
    </TimelineChartContainer>
  );
};
